import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { WebhookEndpoint } from 'src/features/webhook-endpoints/utils/types';
import { useRotateWebhookEndpointSecret } from 'src/features/webhook-endpoints/api/useRotateWebhookEndpointSecret';
import { webhookEndpointsMessages } from 'src/features/webhook-endpoints/utils/messages';
import { LIST_WEBHOOK_ENDPOINTS_RESOURCE } from 'src/features/webhook-endpoints/api/useListWebhookEndpoints';

interface RotateWebhookEndpointSecretModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWebhookEndpoint: WebhookEndpoint;
    children?: React.ReactNode;
}

function RotateWebhookEndpointSecretModal<TData>({
    selectedWebhookEndpoint,
    children,
}: RotateWebhookEndpointSecretModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: rotateSecret } = useRotateWebhookEndpointSecret({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_WEBHOOK_ENDPOINTS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(webhookEndpointsMessages.rotateWebhookEndpointSecretSuccess),
                description: formatMessage(webhookEndpointsMessages.rotateWebhookEndpointSecretSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...webhookEndpointsMessages.rotateSecret },
                subTitle: {
                    ...webhookEndpointsMessages.rotateWebhookEndpointSecretSubtitle,
                },
                icon: ICON_MAP.ROTATE,
                confirmationMessage: { ...webhookEndpointsMessages.confirmRotateWebhookEndpointSecret },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: webhookEndpointsMessages.rotate,
                callbackFn: () => {
                    return (
                        rotateSecret({
                            endpointId: selectedWebhookEndpoint.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default RotateWebhookEndpointSecretModal;
